import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, Validators } from '@angular/forms';
import { SeFeFormService } from 'se-fe-form-service';
import { ChildSaleService } from '../../child-sale.service';
import { TrimPipe } from 'src/shared/pipes/trim.pipe';
import { FormattedDatePipe } from 'src/shared/pipes/formatted-date.pipe';
import { ModalBaseComponent } from '../modal-base.component';
import { take, tap } from 'rxjs/operators';
import { SeFeFormFieldMultiselectComponent } from 'se-fe-form-field-multiselect';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { ChildSale } from 'src/shared/models/child-sale.model';
import { ItemVariation } from 'src/shared/models/item-variation.model';
import { dateAtLeastSevenDaysInTheFutureValidator } from 'src/app/validators/date-in-the-past.validator';
import { SeFeExcludedDates } from 'se-fe-datepicker';
import { DateTime } from 'luxon';
import { PriceVariation } from 'src/shared/models/price-variation.model';

type ParentPaymentMethod = {
  acct_num?: string | number;
};

@Component({
  selector: 'app-linked-sale-modal',
  styleUrls: ['./linked-sale-modal.component.scss'],
  templateUrl: './linked-sale-modal.component.html',
  providers: [TrimPipe, FormattedDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LinkedSaleModalComponent extends ModalBaseComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('creditReason') childSale: SeFeFormFieldMultiselectComponent;
  @Input() processing_fee: string;
  @Input() transaction_fee: string;
  @Input() service_fee_definition_method: string;
  @Input() shared_org_rate: string;
  @Input() shared_org_payment_plan_fee: string;
  @Input() shared_member_rate: string;
  @Input() shared_member_payment_plan_fee: string;
  total: number = 3;

  public excludeStart: string;
  public excludeEnd: string;

  public excludeDates: SeFeExcludedDates[];
  private date: DateTime = DateTime.now();
  private paymentMethodLastFour: string | null = null;

  public creditReasonOptions: any = {
    sections: [
      {
        options: [],
      },
    ],
  };

  public dueDateConfig = {
    label: 'Due Date',
    errorMessages: {
      required: 'Due date is required',
      dateOutOfRange: 'Please choose a date at least 7 days from today',
    },
  };

  public applyCreditConfig = {
    label: 'Apply Credit',
    value: false,
  };

  public creditAmountConfig = {
    label: 'Credit Amount',
    errorMessages: {
      required: 'Please enter a credit amount',
      min: 'Please enter a credit amount greater than zero',
      max: 'Please enter a credit amount that is less than the new order total',
    },
  };

  public creditReasonConfig = {
    label: 'Reason for Credit',
    errorMessages: {
      required: 'Please enter a reason for the credit',
    },
  };

  constructor(
    viewContainerRef: ViewContainerRef,
    detector: ChangeDetectorRef,
    translate: TranslateService,
    fb: FormBuilder,
    el: ElementRef,
    fs: SeFeFormService,
    ChildSaleService: ChildSaleService
  ) {
    super(viewContainerRef, detector, translate, fb, el, fs, ChildSaleService);
    this.createForm();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.fetchCreditReasons();
    this.fetchParentPaymentMethod();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parent_sale_id']?.currentValue) {
      this.fetchParentPaymentMethod();
    }
  }

  //Form
  protected createForm(): void {
    this.multiselectForm = this.fb.group({
      multiselect: [],
    });
    this.form = this.fb.group({
      textarea: [],
      credit_amount: [],
      credit_reason: [],
      apply_credit: [false, []],
      cardholder_authorization: [false],
      due_date: ['', [Validators.required, dateAtLeastSevenDaysInTheFutureValidator()]],
      items: this.fb.array([]),
    });

    this.excludeStart = this.date.plus({ year: 1 }).toFormat('yyyy-MM-dd');
    this.excludeEnd = this.date.plus({ day: 7 }).toFormat('yyyy-MM-dd');

    this.excludeDates = [{ end: this.excludeEnd }, { start: this.excludeStart }];

    this.form.get('apply_credit').valueChanges.subscribe((value) => {
      const creditAmountControl = this.form.get('credit_amount');
      const creditReasonControl = this.form.get('credit_reason');

      if (value) {
        creditAmountControl.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.max(this.getItemsTotal()),
        ]);
        creditReasonControl.setValidators([Validators.required]);
        this.fetchCreditReasons();
      } else {
        creditAmountControl.reset(null, { emitEvent: false });
        creditReasonControl.reset(null, { emitEvent: false });
        creditAmountControl.clearValidators();
        creditReasonControl.clearValidators();
      }

      creditAmountControl.updateValueAndValidity();
      creditReasonControl.updateValueAndValidity();
    });

    this.form.get('due_date').valueChanges.subscribe((value) => {
      const applyCreditControl = this.form.get('apply_credit');
      const creditAmountControl = this.form.get('credit_amount');

      if (applyCreditControl.value) {
        creditAmountControl.setValidators([
          Validators.required,
          Validators.min(0.01),
          Validators.max(this.getItemsTotal()),
        ]);
      } else {
        creditAmountControl.clearValidators();
      }
      creditAmountControl.updateValueAndValidity();
    });

    this.form.get('items').valueChanges.subscribe(() => {
      this.resetPaymentTerms();
    });
  }

  private resetPaymentTerms(): void {
    this.form.get('apply_credit').reset(false);
    this.form.get('due_date').reset('');
  }

  public override getSearchItems(searchItem: string, isUserSearch = true): void {
    this.requestSearchItems(searchItem, isUserSearch, false);
  }

  public onCurrencyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const regex = /^\d+(\.\d{0,2})?$/;

    if (!(regex.test(value) || value === '')) {
      input.value = input.value.slice(0, -1);
    }
  }

  public getItemsTotal(): number {
    const itemsArray = this.getItems();
    return itemsArray.controls.reduce((acc, itemGroup, index) => {
      const itemValue = itemGroup.get('item')?.value;
      const quantity = itemGroup.get('quantity')?.value;

      if (!itemValue || !quantity) {
        return acc;
      }

      const selectedItem = this.searchItems.find((searchItem) => searchItem.id === Number(itemValue.value));
      if (!selectedItem) {
        return acc;
      }

      return acc + this.getPriceOrPriceVariation(selectedItem) * quantity;
    }, 0);
  }

  private roundMoney(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  public getItemAmount(index: number): number {
    const itemGroup = this.getItems().controls[index];
    const itemValue = itemGroup.get('item')?.value;
    const quantity = itemGroup.get('quantity')?.value;

    if (!itemValue || !quantity) {
      return 0;
    }

    const selectedItem = this.searchItems.find((searchItem) => searchItem.id === Number(itemValue.value));
    if (!selectedItem) {
      return 0;
    }

    return this.getPriceOrPriceVariation(selectedItem) * quantity;
  }

  public fetchCreditReasons(): void {
    this.setOrgId();

    this.ChildSaleService.getCreditReasons(this.org_id)
      .pipe(
        take(1),
        tap((response: ApiResponse<{ id: number; title: string }[]>) => {
          this.creditReasonOptions = this.setCreditReasons(response?.result);
        })
      )
      .subscribe({
        next: () => this.detector.detectChanges(),
        error: () => this.detector.detectChanges(),
      });
  }

  public setCreditReasons(reasons: unknown): { sections: { options: { text: string; value: string }[] }[] } {
    const list = Array.isArray(reasons) ? reasons : [];
    const section = list.map((reason: { id: number; title: string }) => ({
      text: reason.title,
      value: reason.id.toString(),
    }));

    return {
      sections: [
        {
          options: section,
        },
      ],
    };
  }

  public nextStep(): void {
    if (this.step !== 2) {
      super.nextStep();
    } else {
      if (this.form.valid) {
        super.nextStep();
      } else {
        const dueDateControl = this.form.get('due_date');
        dueDateControl.markAsTouched();
        dueDateControl.markAsDirty();
        dueDateControl.updateValueAndValidity();

        const applyCredit = this.form.get('apply_credit').value;
        if (applyCredit) {
          const creditAmountControl = this.form.get('credit_amount');
          const creditReasonControl = this.form.get('credit_reason');
          creditAmountControl.markAsDirty();
          creditReasonControl.markAsDirty();
          creditAmountControl.markAsTouched();
          creditReasonControl.markAsTouched();
          creditAmountControl.updateValueAndValidity();
          creditReasonControl.updateValueAndValidity();
        }
      }
    }

    if (this.step === 3 && !this.paymentMethodLastFour) {
      this.fetchParentPaymentMethod();
    }
  }

  public getCustomerFeeAmount() {
    let result: number;

    if (this.getTotal() === 0) {
      return 0;
    }
    switch (this.service_fee_definition_method) {
      case 'shared':
        result =
          this.getTotal() * parseFloat(this.shared_member_rate) + parseFloat(this.shared_member_payment_plan_fee);
        break;
      case 'org_pays_recouped':
        result = this.getTotal() * parseFloat(this.processing_fee) + parseFloat(this.transaction_fee);
        break;
      case 'org_pays':
      default:
        result = 0;
        break;
    }

    return this.roundMoney(result);
  }

  public getOrganizationFeeAmount() {
    let result: number;

    if (this.getTotal() === 0) {
      return 0;
    }
    switch (this.service_fee_definition_method) {
      case 'shared':
        result = this.getTotal() * parseFloat(this.shared_org_rate) + parseFloat(this.shared_org_payment_plan_fee);
        break;
      case 'org_pays_recouped':
        result = 0;
        break;
      case 'org_pays':
      default:
        result = this.getTotal() * parseFloat(this.processing_fee) + parseFloat(this.transaction_fee);
        break;
    }

    return this.roundMoney(result);
  }

  public hasAppliedCredit(): boolean {
    return Boolean(this.form.get('apply_credit')?.value) && Number(this.form.get('credit_amount')?.value) > 0;
  }

  public getTotal(): number {
    return this.roundMoney(
      this.itemsSum() - (this.form.get('apply_credit').value ? this.form.get('credit_amount').value : 0)
    );
  }

  public getPaymentMethodLastFour(): string | null {
    return this.paymentMethodLastFour;
  }

  public getMaskedPaymentMethodLastFour(): string | null {
    return this.paymentMethodLastFour ? `{****${this.paymentMethodLastFour}}` : null;
  }

  getTotalCharged(): number {
    switch (this.service_fee_definition_method) {
      case 'shared':
      case 'org_pays_recouped':
        return this.getTotal() + this.getCustomerFeeAmount();
      case 'org_pays':
      default:
        return this.getTotal();
    }
  }

  public submitForm(): void {
    this.submitIsInProgress = true;
    this.ChildSaleService.createOrder(this.prepareChildSale()).subscribe(
      (response) => {
        if (!response.error || Object.keys(response.error).length === 0) {
          this.submit.emit(response.result);
          this.redirectToOrderDetail(response.result.id);
          this.cleanForm();
        }
        this.submitIsInProgress = false;
      },
      (error) => {
        this.error = true;
        this.submitIsInProgress = false;
      }
    );
  }

  public prepareChildSale(): ChildSale {
    const items = [];
    const creditReasonValue = this.form.get('credit_reason').value?.toString() ?? '';
    const creditReason = this.creditReasonOptions.sections[0].options.find(
      (option) => option.value === creditReasonValue
    );
    this.selectedItems.value.forEach((item) => {
      items.push({
        item_variation_id: Number(item.item.value),
        quantity: item.quantity,
      });
    });
    return {
      parent_sale_id: this.parent_sale_id,
      consent_accepted: this.form.get('cardholder_authorization').value,
      child_items: items,
      due_date: DateTime.fromFormat(this.form.get('due_date').value, 'MM/dd/yyyy').toISODate(),
      credit:
        this.form.get('apply_credit').value ?
          {
            amount: parseFloat(this.form.get('credit_amount').value),
            credit_reason: {
              title: creditReason?.text ?? creditReasonValue,
            },
          }
        : null,
      notes: this.form.get('textarea').value,
    };
  }

  public priceVariationIsActive(item: ItemVariation) {
    return this.getMatchingPriceVariation(item) ? true : false;
  }

  protected override getPriceOrPriceVariation(item: ItemVariation): number {
    if (this.form.get('due_date').value) {
      const matchingVariation = this.getMatchingPriceVariation(item);

      return matchingVariation ? matchingVariation.price_cents / 100 : item.price_cents / 100;
    }

    return item.price_cents / 100;
  }

  private getMatchingPriceVariation(item: ItemVariation): Partial<PriceVariation> | undefined {
    let dueDate = new Date(this.form.get('due_date').value);
    return item.price_variations?.find((v) => {
      const start = new Date(v.start_date);
      const end = new Date(v.end_date);
      return dueDate >= start && dueDate <= end;
    });
  }

  private fetchParentPaymentMethod(): void {
    if (!this.parent_sale_id) {
      return;
    }

    this.ChildSaleService.getOrder(this.parent_sale_id)
      .pipe(take(1))
      .subscribe({
        next: (response: ApiResponse<Record<string, unknown>>) => {
          const order = this.resolveOrderPayload(response);
          this.paymentMethodLastFour = this.extractPaymentMethodLastFour(order);
          this.detector.detectChanges();
        },
        error: () => {
          this.paymentMethodLastFour = null;
          this.detector.detectChanges();
        },
      });
  }

  private resolveOrderPayload(
    response: ApiResponse<Record<string, unknown>> | Record<string, unknown> | null | undefined
  ): Record<string, unknown> | undefined {
    if (!response || typeof response !== 'object') {
      return undefined;
    }

    const wrapped = response as ApiResponse<Record<string, unknown>>;
    if (wrapped.result && typeof wrapped.result === 'object') {
      return wrapped.result;
    }

    const unbound = response as Record<string, unknown>;
    if (Array.isArray(unbound['payments']) || unbound['payment_plan_payment_method'] != null) {
      return unbound;
    }

    return undefined;
  }

  private extractPaymentMethodLastFour(order: Record<string, unknown> | undefined): string | null {
    if (!order) {
      return null;
    }

    const payments = order['payments'] as Array<{ acct_num?: string | number }> | undefined;
    if (Array.isArray(payments)) {
      for (const payment of payments) {
        const lastFour = this.getLastFour(payment?.acct_num);
        if (lastFour) {
          return lastFour;
        }
      }
    }

    const paymentMethod = order['payment_plan_payment_method'] as ParentPaymentMethod | undefined;
    return this.getLastFour(paymentMethod?.acct_num) || null;
  }

  private getLastFour(accountNumber: string | number | undefined | null): string {
    return accountNumber == null ? '' : String(accountNumber).slice(-4);
  }
}
